import { useCallback } from "react";
import { useSearchParams } from "react-router";

const PERSON_PARAM = "person";
const DAY_PARAM = "day";
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface DayTarget {
  employeeId: string;
  date: string;
}

/** The link that opens a person's day in the drawer, on the page given (the grid by default). */
export function dayHref(target: DayTarget, path = "/people/attendance"): string {
  const params = new URLSearchParams({
    month: target.date.slice(0, 7),
    [PERSON_PARAM]: target.employeeId,
    [DAY_PARAM]: target.date,
  });
  return `${path}?${params.toString()}`;
}

/**
 * The day open in the drawer, kept in the URL (?person=…&day=2026-10-12) so a review item, a
 * payroll blocker or a message can link straight to it. Without a person in the URL the day is
 * the given person's (one's own month, a profile).
 */
export function useDayParam(fallbackEmployeeId?: string) {
  const [params, setParams] = useSearchParams();
  const date = params.get(DAY_PARAM);
  const employeeId = params.get(PERSON_PARAM) ?? fallbackEmployeeId ?? null;
  const target: DayTarget | null =
    date && ISO_DATE.test(date) && employeeId ? { employeeId, date } : null;

  const open = useCallback(
    (next: DayTarget) => {
      setParams((current) => {
        const updated = new URLSearchParams(current);
        updated.set(DAY_PARAM, next.date);
        if (next.employeeId === fallbackEmployeeId) updated.delete(PERSON_PARAM);
        else updated.set(PERSON_PARAM, next.employeeId);
        return updated;
      });
    },
    [setParams, fallbackEmployeeId],
  );

  const close = useCallback(() => {
    setParams((current) => {
      const updated = new URLSearchParams(current);
      updated.delete(DAY_PARAM);
      updated.delete(PERSON_PARAM);
      return updated;
    });
  }, [setParams]);

  return { target, open, close };
}
